import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { ProspectFooterFields } from '../redux/reducers';
import { socket } from '../services';
import { PracticeProspectDetails } from '../types';

export enum AITwinCreationStatus {
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

enum AITwinEvent {
  AITWIN_CREATION_STARTED = 'aitwin:creation:started',
  AITWIN_CREATION_COMPLETED = 'aitwin:creation:completed',
  AITWIN_CREATION_FAILED = 'aitwin:creation:failed',
}

type AITwinEventResponse = {
  data: {
    prospect: PracticeProspectDetails;
  };
};

interface UseAITwinSocketEventsProps {
  setAiTwinDetails: Dispatch<SetStateAction<Omit<PracticeProspectDetails, ProspectFooterFields> | undefined>>;
  setAiTwinCreationStatus: Dispatch<SetStateAction<AITwinCreationStatus | undefined>>;
}

const useAITwinSocketEvents = ({ setAiTwinDetails, setAiTwinCreationStatus }: UseAITwinSocketEventsProps) => {
  const handleUpdateAITwinStatus = useCallback(
    (status: AITwinCreationStatus) => {
      setAiTwinCreationStatus(status);
    },
    [setAiTwinCreationStatus]
  );

  const handleCreateAITwinCompleted = useCallback(
    (response: AITwinEventResponse) => {
      handleUpdateAITwinStatus(AITwinCreationStatus.COMPLETED);
      setAiTwinDetails(response.data.prospect);
    },
    [handleUpdateAITwinStatus]
  );

  useEffect(() => {
    socket.on(AITwinEvent.AITWIN_CREATION_STARTED, () => handleUpdateAITwinStatus(AITwinCreationStatus.PROCESSING));
    socket.on(AITwinEvent.AITWIN_CREATION_COMPLETED, handleCreateAITwinCompleted);
    socket.on(AITwinEvent.AITWIN_CREATION_FAILED, () => handleUpdateAITwinStatus(AITwinCreationStatus.FAILED));

    // Clean up the event listeners when the component unmounts
    return () => {
      socket.off(AITwinEvent.AITWIN_CREATION_STARTED, handleUpdateAITwinStatus);
      socket.off(AITwinEvent.AITWIN_CREATION_COMPLETED, handleCreateAITwinCompleted);
      socket.off(AITwinEvent.AITWIN_CREATION_FAILED, handleUpdateAITwinStatus);
    };
  }, [socket, handleCreateAITwinCompleted, handleUpdateAITwinStatus]);
};

export default useAITwinSocketEvents;
